import { Icon } from "@pepperstone/pepper-ui-kit";
import { Table } from "@tanstack/react-table";

type PaginationProps<T> = {
  table: Table<T>;
  pageSize?: number;
  labelForShowing?: string;
  labelForOf?: string;
  isRightToLeft: boolean;
  totalCount?: number;
};

// Helper function to calculate which pages to display with smart pagination
const getVisiblePages = (
  pageIndex: number,
  pageCount: number,
): (number | string)[] => {
  // If 6 or fewer pages, show all pages
  if (pageCount <= 6) {
    return Array.from({ length: pageCount }, (_, i) => i);
  }

  // For more than 6 pages, use smart pagination with ellipsis
  const visiblePages: (number | string)[] = [];

  if (pageIndex < 3) {
    // First 3 pages: show first 4, ellipsis, last
    visiblePages.push(0, 1, 2, 3, "...", pageCount - 1);
  } else if (pageIndex >= pageCount - 4) {
    // Last pages: show first, ellipsis, last 4
    visiblePages.push(
      0,
      "...",
      pageCount - 4,
      pageCount - 3,
      pageCount - 2,
      pageCount - 1,
    );
  } else {
    // Middle pages: show first, ellipsis, current, current+1, ellipsis, last
    visiblePages.push(0, "...", pageIndex, pageIndex + 1, "...", pageCount - 1);
  }

  return visiblePages;
};

export const Pagination = <T,>({
  table,
  pageSize = 10,
  labelForShowing = "Showing",
  labelForOf = "of",
  isRightToLeft = false,
  totalCount: totalCountProp,
}: PaginationProps<T>) => {
  const pageIndex = table.getState().pagination.pageIndex;
  const pageCount = table.getPageCount();
  const totalCount = totalCountProp ?? table.getRowCount();

  if (totalCount <= 0) {
    return null;
  }
  const startIndex = pageIndex * pageSize + 1;
  const endIndex = Math.min(pageIndex * pageSize + pageSize, totalCount);
  const paginationText = `${labelForShowing} ${startIndex}-${endIndex} ${labelForOf} ${totalCount.toLocaleString()}`;

  const visiblePages = getVisiblePages(pageIndex, pageCount);

  return (
    <div
      className="content-start gap-2 lg:grid lg:grid-cols-3 lg:align-middle"
      data-testid="pagination"
    >
      <div className="typo-body-xs lg:typo-body-md content-center pb-2 text-center text-text-tertiary lg:pb-0 lg:text-left">
        {paginationText}
      </div>
      {pageCount > 1 && (
        <div className="flex w-full justify-center">
          <div className="flex items-center justify-center gap-2 text-xs">
            <button
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-stroke-primary bg-surface-primary hover:bg-surface-tonal-default disabled:bg-surface-disabled disabled:hover:bg-surface-disabled lg:h-10 lg:w-10"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              aria-label="Previous page"
            >
              <Icon
                color={!table.getCanPreviousPage() ? "secondary" : "black"}
                icon={isRightToLeft ? "arrow-right" : "arrow-left"}
                size={12}
              />
            </button>
            {visiblePages.map((page, index) => {
              if (page === "...") {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    className="typo-label-xs lg:typo-label-sm flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-stroke-primary bg-surface-primary text-text-secondary lg:h-10 lg:w-10"
                    aria-hidden="true"
                  >
                    ...
                  </span>
                );
              }
              const pageNum = page as number;
              const isSelected = pageIndex === pageNum;
              return (
                <button
                  className={`typo-label-xs lg:typo-label-sm flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border lg:h-10 lg:w-10 ${
                    isSelected
                      ? "border-stroke-inverse-primary bg-surface-inverse-primary text-text-inverse-primary"
                      : "border-stroke-primary bg-surface-primary text-text-primary hover:bg-surface-tonal-default"
                  }`}
                  key={pageNum}
                  onClick={() => table.setPageIndex(pageNum)}
                  aria-label={`Page ${pageNum + 1}${isSelected ? " (current)" : ""}`}
                  aria-current={isSelected ? "page" : undefined}
                >
                  {pageNum + 1}
                </button>
              );
            })}
            <button
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-stroke-primary bg-surface-primary hover:bg-surface-tonal-default disabled:bg-surface-disabled disabled:hover:bg-surface-disabled lg:h-10 lg:w-10"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              aria-label="Next page"
            >
              <Icon
                color={!table.getCanNextPage() ? "secondary" : "black"}
                icon={isRightToLeft ? "arrow-left" : "arrow-right"}
                size={12}
              />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
